import { restoreProject } from '../utils/projectPreference';
import React, { createContext, useState, useEffect, useContext } from 'react';
import backendApi from '../services/backendApi';
import { readAuthStorage, removeAuthStorage, writeAuthStorage } from '../utils/authStorage';

// Création du contexte d'authentification
const AuthContext = createContext(null);

/**
 * Provider d'authentification pour l'application
 * Gère l'état global de l'utilisateur, le login/logout et la persistance de session
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Vérifie si une session existe au chargement de l'application
   */
  useEffect(() => {
    const checkSession = async () => {
      try {
        const storedUser = readAuthStorage('odoo_user');
        const storedToken = readAuthStorage('odoo_auth_token');
        const checkedToken = backendApi.getAuthToken();

        if (storedUser || storedToken) {
          // Vérifier que la session backend est toujours valide
          // et utiliser les données fraîches (incluant companyId)
          try {
            const freshUser = await backendApi.getCurrentUser();
            console.log('[Auth] freshUser from /me:', freshUser);
            writeAuthStorage('odoo_user', JSON.stringify(freshUser));
            restoreProject(freshUser, true);
            setUser(freshUser);
          } catch (err) {
            // Ne pas effacer une nouvelle session créée pendant cette vérification.
            if (backendApi.getAuthToken() === checkedToken) {
              removeAuthStorage('odoo_user');
              removeAuthStorage('odoo_auth_token');
            }
          }
        }
      } catch (err) {
        console.error('Erreur lors de la vérification de session:', err);
        removeAuthStorage('odoo_user');
        removeAuthStorage('odoo_auth_token');
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    // Écouter les événements de session expirée (depuis l'intercepteur axios)
    const handleSessionExpired = () => {
      setUser(null);
      setError(null);
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, []);

  /**
   * Connexion de l'utilisateur
   * @param {string} username - Nom d'utilisateur Odoo
   * @param {string} password - Mot de passe Odoo
   * @returns {Promise<Object>} Données de l'utilisateur connecté
   */
  const login = async (username, password) => {
    try {
      setLoading(true);
      setError(null);

      // Appeler le backend qui utilise des sessions
      const response = await backendApi.login(username, password);

      const userData = response.user;

      // Sauvegarder l'affichage utilisateur ; le service API conserve séparément le jeton signé.
      writeAuthStorage('odoo_user', JSON.stringify(userData));

      restoreProject(userData);
      setUser(userData);

      return userData;
    } catch (err) {
      const errorMessage = err.message || 'Erreur lors de la connexion';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Déconnexion de l'utilisateur
   */
  const logout = async () => {
    try {
      // Appeler le backend pour détruire la session
      await backendApi.logout();
    } catch (err) {
      console.error('Erreur lors de la déconnexion:', err);
    } finally {
      // Nettoyer le localStorage
      removeAuthStorage('odoo_user');
      setUser(null);
      setError(null);
    }
  };

  /**
   * Mettre à jour les informations de l'utilisateur
   * @param {Object} userData - Nouvelles données utilisateur
   */
  const updateUser = (userData) => {
    setUser(userData);
    writeAuthStorage('odoo_user', JSON.stringify(userData));
  };

  const isInternalUser = user?.isInternal === true;
  console.log('[Auth] isInternal:', user?.isInternal, '| isInternalUser:', isInternalUser);

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user,
    isInternalUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook personnalisé pour accéder au contexte d'authentification
 * @returns {Object} Contexte d'authentification
 */
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }

  return context;
};

export default AuthContext;
